<script>
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    timelineTracksState,
    removeTrack
} from "@app/store/timelineTracksStore.svelte.js";

/**
 * Left-gutter list of track names. Renders one row per timeline track,
 * matching the heights set by the parent Timeline shell so labels stay
 * aligned with their tracks.
 *
 * Primary track (always present) is read from `projectState.selected`;
 * additional tracks come from `timelineTracksState.additional`.
 *
 * @typedef {Object} Props
 * @property {number} rowHeight   - height of each track row in px
 * @property {number} topPadding  - distance from top of the labels container
 *                                  to the first row (matches ruler height)
 */

/** @type {Props} */
let { rowHeight = 60, topPadding = 22 } = $props();

let tracks = $derived.by(() => {
    const out = [];
    const primaryIdx = projectState.selected;
    if (eventGroupState[primaryIdx]) {
        out.push({ index: primaryIdx, group: eventGroupState[primaryIdx], primary: true });
    }
    for (let i = 0; i < timelineTracksState.additional.length; i += 1) {
        const idx = timelineTracksState.additional[i];
        if (idx === primaryIdx) continue;
        const group = eventGroupState[idx];
        if (!group) continue;
        out.push({ index: idx, group, primary: false });
    }
    return out;
});

const typeIcon = {
    subtitle: "bi-card-text",
    transcription: "bi-mic",
    translation: "bi-translate",
    "audio description": "bi-headphones"
};

function iconFor(group) {
    return typeIcon[group?.type] || "bi-list-task";
}
</script>

<div class="track-labels" style="padding-top: {topPadding}px;">
    {#each tracks as track (track.index)}
        <div class="track-label" style="height: {rowHeight}px;" title={track.group.name}>
            <div class="track-label-inner">
                <i class="bi {iconFor(track.group)}" aria-hidden="true"></i>
                <span class="name">{track.group.name || `Track ${track.index + 1}`}</span>
                {#if track.primary}
                    <span class="badge bg-primary primary-badge">primary</span>
                {:else}
                    <button
                        type="button"
                        class="btn btn-sm btn-link remove-btn"
                        aria-label={`Remove ${track.group.name} from timeline`}
                        title="Remove from timeline"
                        onclick={() => removeTrack(track.index)}>
                        <i class="bi bi-x-lg"></i>
                    </button>
                {/if}
            </div>
        </div>
    {/each}
</div>

<style>
.track-labels {
    background: var(--bs-tertiary-bg);
    border-right: 1px solid var(--bs-border-color);
    color: var(--bs-body-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    user-select: none;
    width: 100%;
}

.track-label {
    align-items: stretch;
    border-bottom: 1px solid var(--bs-border-color-translucent, var(--bs-border-color));
    display: flex;
    padding: 4px 8px;
}

.track-label-inner {
    align-items: center;
    display: flex;
    flex: 1;
    gap: 6px;
    overflow: hidden;
}

.track-label-inner .name {
    flex: 1;
    font-size: 0.8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.primary-badge {
    font-size: 0.6rem;
    padding: 2px 6px;
}

.remove-btn {
    color: var(--bs-secondary-color);
    padding: 0;
    line-height: 1;
}

.remove-btn:hover {
    color: #ff6b6b;
}
</style>
